import { useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../utils/permissionUtils';
import { isOrgAdminRole } from '../utils/roleUtils';
import { EditLogTable } from './EditLog';

/** Whether this user may read the edit log: the Org Admin and the Finance Manager. */
export function useCanSeeHistory() {
  const { user } = useAuth();
  return !!user?.isSystemAdmin || (user?.roles || []).some(isOrgAdminRole) || hasAnyRole(user, ['FIN_MGR']);
}

/**
 * A small History button for a document's or master record's row: opens everything the edit log holds for it (its
 * lines included), newest first. Shows nothing to roles that can't read the edit log.
 *
 * @param {string} documentType the record's class name, as the edit log keys it ('SaleBill', 'Customer', ...)
 * @param {number} documentId
 * @param {string} [label] how the dialog names it ('Sale bill SB-2026-10-0001')
 */
function HistoryButton({ documentType, documentId, label, size = 'sm', className = '' }) {
  const allowed = useCanSeeHistory();
  const [entries, setEntries] = useState(null);
  const [show, setShow] = useState(false);
  const [problem, setProblem] = useState('');

  if (!allowed || !documentId) return null;

  const open = async (e) => {
    e?.stopPropagation?.();
    setShow(true);
    setEntries(null);
    setProblem('');
    try {
      const r = await axios.get(`/api/edit-log/documents/${documentType}/${documentId}`);
      setEntries(r.data);
    } catch (error) {
      setProblem(typeof error.response?.data === 'string' && error.response.data ? error.response.data : 'Could not load the history');
    }
  };

  return (
    <>
      <Button size={size} variant="outline-secondary" className={className} title="Who created and changed this, and when" onClick={open}>
        <i className="bi bi-clock-history" /><span className="visually-hidden">History</span>
      </Button>
      {show && (
        <Modal show onHide={() => setShow(false)} size="xl" onClick={(e) => e.stopPropagation()}>
          <Modal.Header closeButton><Modal.Title as="h5">History{label ? `: ${label}` : ''}</Modal.Title></Modal.Header>
          <Modal.Body>
            {problem && <div className="text-danger">{problem}</div>}
            {!problem && !entries && <div className="text-center py-3"><Spinner animation="border" /></div>}
            {entries && (
              <>
                <EditLogTable entries={entries} showWhat={false}
                  emptyText="Nothing recorded yet: the edit log only holds changes made since it was turned on." />
                {entries.length >= 500 && <div className="small text-muted">The latest 500 entries; Org Admin &rsaquo; Edit Log has the rest.</div>}
              </>
            )}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
}

export default HistoryButton;
